import type { Role } from '@sentra/contracts';
import { LOCALES } from '@sentra/contracts';
import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { sectionsFor } from '@/app/navigation';
import { useTheme } from '@/app/providers/theme';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/design/components/command';
import { useLocale } from '@/i18n';

/**
 * The command palette: every section and every shell action, reachable from the keyboard alone.
 *
 * **It exists because the gate is worked at speed.** An operator with somebody standing in front of
 * them should not have to find a menu with a mouse, and the sections a shift actually uses are two
 * or three of the four. The palette is the fast path to all of them and to the three actions that
 * otherwise live behind a disclosure.
 *
 * **It reads the same section table the sidebar does.** A section offered in one and missing from
 * the other is the classic version of this bug, and it is invisible to whoever uses the other one.
 */
export function CommandPalette({
  open,
  onOpenChange,
  role,
  onSwitchOperator,
  onSignOut,
}: Readonly<{
  open: boolean;
  onOpenChange: (next: boolean) => void;
  role: Role;
  onSwitchOperator: () => void;
  onSignOut: () => void;
}>): ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setLocale } = useLocale();
  const { setPreference } = useTheme();

  function run(action: () => void): void {
    onOpenChange(false);
    action();
  }

  return (
    <CommandDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('palette.title')}
      description={t('palette.description')}
      closeLabel={t('common.close')}
    >
      <CommandInput placeholder={t('palette.placeholder')} />
      <CommandList>
        <CommandEmpty>{t('palette.empty')}</CommandEmpty>
        <CommandGroup heading={t('palette.groupNavigation')}>
          {sectionsFor(role).map((section) => {
            const label = t(`nav.${section.id}`);
            return (
              <CommandItem
                key={section.id}
                value={`${section.id} ${label}`}
                onSelect={() => {
                  run(() => {
                    void navigate(section.path);
                  });
                }}
              >
                <section.icon aria-hidden className="size-4" />
                {label}
              </CommandItem>
            );
          })}
        </CommandGroup>
        <CommandGroup heading={t('palette.groupActions')}>
          <CommandItem
            value={`theme light ${t('theme.light')}`}
            onSelect={() => {
              run(() => {
                setPreference('light');
              });
            }}
          >
            {t('theme.light')}
          </CommandItem>
          <CommandItem
            value={`theme dark ${t('theme.dark')}`}
            onSelect={() => {
              run(() => {
                setPreference('dark');
              });
            }}
          >
            {t('theme.dark')}
          </CommandItem>
          {LOCALES.map((candidate) => {
            const label = t(`language.${candidate}`);
            return (
              <CommandItem
                key={candidate}
                value={`language ${candidate} ${label}`}
                onSelect={() => {
                  run(() => {
                    setLocale(candidate);
                  });
                }}
              >
                {label}
              </CommandItem>
            );
          })}
          <CommandItem
            value={`operator ${t('session.switchOperator')}`}
            onSelect={() => {
              run(onSwitchOperator);
            }}
          >
            {t('session.switchOperator')}
          </CommandItem>
          <CommandItem
            value={`signout ${t('session.signOut')}`}
            onSelect={() => {
              run(onSignOut);
            }}
          >
            {t('session.signOut')}
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}

/**
 * The chord that opens the palette, bound on the document so it works wherever focus is.
 *
 * It is deliberately NOT bound while a text field is being typed into with a modifier-free key: the
 * binding requires Control or Command, so it cannot swallow a character. That matters more here
 * than in most applications, because AD-32's scanner input is a burst of plain keystrokes and
 * anything that intercepts bare keys at the document level is competing with it.
 *
 * **And it is not bound at all while the terminal is locked.** The shell stays mounted under the
 * re-authentication overlay, which is what preserves the operator's half-filled form — so a
 * document-level shortcut left listening would let anybody walking past open the palette and
 * choose "sign out", which reaches `auth.logout` (a route the matrix deliberately lets pass
 * `idle_locked`) and discards exactly the work the lock exists to protect. A lock somebody can
 * press a key past is not a lock.
 */
export function usePaletteShortcut(onOpen: () => void, enabled: boolean): void {
  useEffect(() => {
    if (!enabled) {
      return undefined;
    }
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key.toLowerCase() === 'k' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        onOpen();
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [onOpen, enabled]);
}
